/**
 * Rewrap-from-runs — produce a fresh `preWrappedLines` for a text node.
 *
 * Two entry points:
 *
 *   - `rewrapFromContent(node, ctx)` — starts from `content` runs alone.
 *     Used after the user commits an edit: Lexical has updated the runs,
 *     but the captured browser layout is gone. We re-derive `preWrappedLines`
 *     so the canvas's fast path keeps painting per-run styles instead of
 *     falling back to a flat node-level style.
 *
 *   - `rewrapTextNodesFromRuns(nodes, ctx)` — the eval-spike entry. Walks
 *     captured `preWrappedLines`, flattens to runs, and re-wraps. Kept here
 *     so the eval and production share the same tokenizer / line-breaker /
 *     baseline math.
 *
 * Both entry points produce the same captured-line shape, so drawCanvas's
 * existing fast path paints them without modification.
 */

import {
  type LayoutNode,
  type RichTextRun,
  type TextOptions,
} from '@backend/lib/db/typeRoots/layoutNode'
import { type Color } from '@backend/lib/db/typeRoots/color'

type CapturedRun = NonNullable<NonNullable<TextOptions['preWrappedLines']>[number]['runs']>[number]
type CapturedLine = NonNullable<TextOptions['preWrappedLines']>[number]

type FlatRun = {
  text: string
  fontFamily: string
  fontSize: number
  fontWeight: number
  fontStyle: 'normal' | 'italic'
  color: Color
}

type Token = {
  text: string
  width: number
  isWS: boolean
  isHardBreak: boolean
  run: FlatRun
}

const HARD_BREAK_FILL_THRESHOLD = 0.85

const fontStyleOf = (s: string): 'normal' | 'italic' =>
  s.toLowerCase().includes('italic') ? 'italic' : 'normal'

const parseFontSize = (s: string): number => {
  const m = /([\d.]+)/.exec(s)
  return m ? parseFloat(m[1]!) : NaN
}

const parseFontWeight = (s: string): number => {
  const n = parseInt(s, 10)
  if (Number.isFinite(n)) return n
  if (s === 'bold') return 700
  if (s === 'normal') return 400
  return 400
}

const parseColor = (s: string): Color => {
  const m = /rgba?\(([^)]+)\)/.exec(s)
  if (!m) return { r: 0, g: 0, b: 0, a: 255 }
  const parts = m[1]!.split(',').map((p) => parseFloat(p.trim()))
  const [r = 0, g = 0, b = 0, a = 1] = parts
  return { r: Math.round(r), g: Math.round(g), b: Math.round(b), a: Math.round(a * 255) }
}

const runFont = (r: FlatRun): string =>
  `${r.fontStyle} ${r.fontWeight} ${r.fontSize}px "${r.fontFamily}"`

/** Measure where the typographic baseline sits, relative to the line-box top,
 *  for a given font + line-height. Canvas's `fontBoundingBoxAscent` overshoots
 *  the typographic ascent for display faces; we probe via the DOM to derive
 *  the CSS baseline directly. Cached per (font, lineHeight). The cache is
 *  invalidated when web fonts finish loading — pre-load probes return
 *  fallback-font metrics that no longer apply once the real font swaps in. */
const baselineOffsetCache = new Map<string, number>()
let fontsReadyHooked = false
function ensureFontsReadyInvalidation() {
  if (fontsReadyHooked) return
  if (typeof document === 'undefined' || !document.fonts) return
  fontsReadyHooked = true
  document.fonts.ready
    .then(() => {
      baselineOffsetCache.clear()
    })
    .catch(() => {})
}
function measureCssBaselineOffset(font: string, lineHeight: number): number {
  const key = `${font}|${lineHeight}`
  const cached = baselineOffsetCache.get(key)
  if (cached !== undefined) return cached

  // Headless / SSR / pre-mount contexts: no document body to probe against.
  // Return 0 — the caller's font-metric path treats the baseline-lift as a
  // small constant offset, and getting it wrong by a few px is preferable
  // to throwing during eval/offscreen rendering.
  if (typeof document === 'undefined' || !document.body) return 0

  ensureFontsReadyInvalidation()

  const probe = document.createElement('div')
  probe.style.cssText = [
    'position:absolute',
    'visibility:hidden',
    'top:0',
    'left:0',
    'margin:0',
    'padding:0',
    'border:0',
    `font:${font}`,
    `line-height:${lineHeight}`,
    'white-space:nowrap',
  ].join(';')
  const text = document.createElement('span')
  text.textContent = 'M'
  const marker = document.createElement('span')
  marker.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline'
  probe.appendChild(text)
  probe.appendChild(marker)
  document.body.appendChild(probe)
  const probeTop = probe.getBoundingClientRect().top
  const baselineTop = marker.getBoundingClientRect().top
  document.body.removeChild(probe)
  const offset = baselineTop - probeTop
  baselineOffsetCache.set(key, offset)
  return offset
}

const flatFromContent = (runs: RichTextRun[]): FlatRun[] => {
  // Defensive: legacy unmigrated rows may still carry content as a string.
  // Treat that as an empty flat sequence — caller falls back to no preWrap.
  if (!Array.isArray(runs)) return []
  return runs.map((r) => ({
    text: r.text,
    fontFamily: r.fontFamily,
    fontSize: r.fontSize,
    fontWeight: r.fontWeight,
    fontStyle: r.fontStyle,
    color: r.fontColor,
  }))
}

/** Flatten captured per-line layout back into a flat run sequence. Splices
 *  in hard breaks for lines that ended early (under-filled), and inter-word
 *  spaces for lines the browser broke softly. */
function flattenLinesToRuns(
  lines: CapturedLine[],
  innerW: number,
  options: TextOptions,
): FlatRun[] {
  const flat: FlatRun[] = []

  const styleEq = (a: FlatRun, b: FlatRun) =>
    a.fontFamily === b.fontFamily &&
    a.fontSize === b.fontSize &&
    a.fontWeight === b.fontWeight &&
    a.fontStyle === b.fontStyle &&
    a.color.r === b.color.r &&
    a.color.g === b.color.g &&
    a.color.b === b.color.b &&
    a.color.a === b.color.a

  const pushRun = (next: FlatRun) => {
    const prev = flat[flat.length - 1]
    if (prev && styleEq(prev, next)) prev.text += next.text
    else flat.push(next)
  }

  const fallbackRun = (text: string): FlatRun => ({
    text,
    fontFamily: options.fontFamily,
    fontSize: options.fontSize,
    fontWeight: options.fontWeight,
    fontStyle: options.fontStyle,
    color: options.fontColor,
  })

  const runFromCaptured = (cr: CapturedRun): FlatRun => ({
    text: cr.text,
    fontFamily: cr.fontFamily ? cr.fontFamily.replace(/^['"]|['"]$/g, '') : options.fontFamily,
    fontSize:
      typeof (cr as unknown as { fontSize?: number | string }).fontSize === 'number'
        ? (cr.fontSize as unknown as number)
        : typeof (cr as unknown as { fontSize?: number | string }).fontSize === 'string'
          ? parseFontSize((cr as unknown as { fontSize: string }).fontSize)
          : options.fontSize,
    fontWeight:
      typeof (cr as unknown as { fontWeight?: number | string }).fontWeight === 'number'
        ? (cr.fontWeight as unknown as number)
        : typeof (cr as unknown as { fontWeight?: number | string }).fontWeight === 'string'
          ? parseFontWeight((cr as unknown as { fontWeight: string }).fontWeight)
          : options.fontWeight,
    fontStyle:
      cr.fontStyle === 'italic' || cr.fontStyle === 'normal'
        ? cr.fontStyle
        : typeof cr.fontStyle === 'string'
          ? fontStyleOf(cr.fontStyle as unknown as string)
          : options.fontStyle,
    color:
      cr.color && typeof cr.color === 'object' && 'r' in cr.color
        ? (cr.color as Color)
        : typeof cr.color === 'string'
          ? parseColor(cr.color as unknown as string)
          : options.fontColor,
  })

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li]!
    const runsOnLine: FlatRun[] = []
    if (line.runs && line.runs.length > 0) {
      for (const cr of line.runs) runsOnLine.push(runFromCaptured(cr))
    } else {
      runsOnLine.push(fallbackRun(line.text))
    }

    for (let i = 0; i < runsOnLine.length; i++) {
      const r = runsOnLine[i]!
      pushRun(r)
      const nextSameLine = runsOnLine[i + 1]
      if (nextSameLine) {
        const endsSpace = /\s$/.test(r.text)
        const startsSpace = /^\s/.test(nextSameLine.text)
        if (!endsSpace && !startsSpace) pushRun(fallbackRun(' '))
      }
    }

    if (li < lines.length - 1) {
      const filled = (line.width ?? 0) / Math.max(1, innerW)
      const isHardBreak = filled < HARD_BREAK_FILL_THRESHOLD
      pushRun(fallbackRun(isHardBreak ? '\n' : ' '))
    }
  }

  return flat
}

function tokenize(flat: FlatRun[], ctx: CanvasRenderingContext2D): Token[] {
  const out: Token[] = []
  for (const run of flat) {
    ctx.font = runFont(run)
    const parts = run.text.split(/(\n|\s+)/).filter((p) => p.length > 0)
    for (const part of parts) {
      if (part === '\n') {
        out.push({ text: '\n', width: 0, isWS: true, isHardBreak: true, run })
      } else if (/^\s+$/.test(part)) {
        out.push({
          text: part,
          width: ctx.measureText(part).width,
          isWS: true,
          isHardBreak: false,
          run,
        })
      } else {
        out.push({
          text: part,
          width: ctx.measureText(part).width,
          isWS: false,
          isHardBreak: false,
          run,
        })
      }
    }
  }
  return out
}

function breakIntoLines(tokens: Token[], maxWidth: number): Token[][] {
  const lines: Token[][] = []
  let current: Token[] = []
  let currentWidth = 0
  let pendingWS: Token[] = []

  const flushLine = () => {
    pendingWS = []
    lines.push(current)
    current = []
    currentWidth = 0
  }

  for (const tok of tokens) {
    if (tok.isHardBreak) {
      flushLine()
      continue
    }
    if (tok.isWS) {
      pendingWS.push(tok)
      continue
    }
    const wsWidth = pendingWS.reduce((s, t) => s + t.width, 0)
    if (current.length > 0 && currentWidth + wsWidth + tok.width > maxWidth) {
      flushLine()
      current.push(tok)
      currentWidth += tok.width
      pendingWS = []
    } else {
      for (const ws of pendingWS) {
        current.push(ws)
        currentWidth += ws.width
      }
      pendingWS = []
      current.push(tok)
      currentWidth += tok.width
    }
  }
  if (current.length > 0) lines.push(current)
  return lines
}

/** Layout hints inferred from the existing captured `preWrappedLines`. The
 *  browser's `getClientRects()` y values follow visual-ink-extent semantics
 *  that don't match canvas's `fontBoundingBoxAscent`-based formula, so any
 *  rewrap that re-derives line.y from font metrics drifts a few px per line
 *  vs. the captured render. Inheriting `firstLineY` and `spacing` directly
 *  from the captured layout makes the rewrap match exactly. */
type LayoutHints = {
  /** Visual top of the first line (the browser's `line[0].y`). When null,
   *  the rewrap falls back to a font-metric-derived position — fine for
   *  fresh content but not pixel-accurate against a captured baseline. */
  firstLineY: number | null
  /** Distance between consecutive line tops. When null, the rewrap stacks
   *  lines by `maxFontSize * options.lineHeight`. */
  spacing: number | null
  /** Per-line x captured from the browser. Multi-flow layouts (e.g. an
   *  inline icon at x=0 followed by title/body lines at x=59) encode their
   *  positioning here — recomputing from horizontalAlign alone collapses
   *  the icon and content lines to the same x. */
  lineXs: number[] | null
}

function inferLayoutHints(captured: CapturedLine[] | undefined): LayoutHints {
  if (!captured || captured.length === 0) return { firstLineY: null, spacing: null, lineXs: null }
  const firstLineY = captured[0]!.y
  const lineXs = captured.map((l) => l.x)
  if (captured.length < 2) return { firstLineY, spacing: null, lineXs }
  const deltas: number[] = []
  for (let i = 0; i < captured.length - 1; i++) {
    const delta = captured[i + 1]!.y - captured[i]!.y
    if (delta > 0) deltas.push(delta)
  }
  if (deltas.length === 0) return { firstLineY, spacing: null, lineXs }
  deltas.sort((a, b) => a - b)
  return { firstLineY, spacing: deltas[Math.floor(deltas.length / 2)]!, lineXs }
}

type BuiltLine = {
  line: CapturedLine
  maxFontSize: number
}

function buildCapturedLine(
  tokens: Token[],
  options: TextOptions,
  innerW: number,
  lineY: number,
  lineHeightPx: number,
  ctx: CanvasRenderingContext2D,
): BuiltLine | null {
  if (tokens.length === 0) return null

  let endIdx = tokens.length
  while (endIdx > 0 && tokens[endIdx - 1]!.isWS) endIdx--
  if (endIdx === 0) return null
  const live = tokens.slice(0, endIdx)

  type RunBuild = { text: string; run: Token['run'] }
  const merged: RunBuild[] = []
  for (const tok of live) {
    const last = merged[merged.length - 1]
    if (last && last.run === tok.run) last.text += tok.text
    else merged.push({ text: tok.text, run: tok.run })
  }

  const runs: CapturedRun[] = []
  let cursorX = 0
  let maxFontSize = 0
  for (const m of merged) {
    const f = runFont(m.run)
    ctx.font = f
    runs.push({
      text: m.text,
      x: cursorX,
      fontFamily: m.run.fontFamily,
      fontSize: m.run.fontSize,
      fontWeight: m.run.fontWeight,
      fontStyle: m.run.fontStyle,
      color: m.run.color,
    } as CapturedRun)
    cursorX += ctx.measureText(m.text).width
    if (m.run.fontSize > maxFontSize) maxFontSize = m.run.fontSize
  }
  const lineWidth = cursorX

  const padLeft = options.paddingLeft ?? 0
  const slack = Math.max(0, innerW - lineWidth)
  let lineX: number
  switch (options.horizontalAlign) {
    case 'right':
      lineX = padLeft + slack
      break
    case 'center':
      lineX = padLeft + slack / 2
      break
    default:
      lineX = padLeft
      break
  }

  return {
    maxFontSize,
    line: {
      text: merged.map((m) => m.text).join(''),
      x: lineX,
      y: lineY,
      width: lineWidth,
      height: lineHeightPx,
      runs,
    } as CapturedLine,
  }
}

function buildLinesFromFlat(
  flat: FlatRun[],
  options: TextOptions,
  innerW: number,
  innerH: number,
  padTop: number,
  ctx: CanvasRenderingContext2D,
  hints: LayoutHints,
): CapturedLine[] {
  const tokens = tokenize(flat, ctx)
  const lineGroups = breakIntoLines(tokens, innerW)
  const out: CapturedLine[] = []

  // Two-pass build: first lay out each line with a tentative y, learning the
  // per-line max font size as we go; then derive the actual y from either
  // captured hints (preferred) or a font-metric fallback. The font-metric
  // fallback uses CSS baseline + bounding ascent — accurate for fresh
  // content; the captured hints are accurate for round-trip rewraps.
  const tentativeLines: BuiltLine[] = []
  for (const group of lineGroups) {
    // An empty group is a blank line (`\n\n` from the editor, or a
    // hard-break-only run). Preserve it as a no-content line so the
    // blank paragraph doesn't disappear on rewrap. Use the node's
    // default font size for height — there's no glyph content to
    // derive from.
    if (group.length === 0 || group.every((t) => t.isWS)) {
      const padLeft = options.paddingLeft ?? 0
      tentativeLines.push({
        maxFontSize: options.fontSize,
        line: {
          text: '',
          x: padLeft,
          y: 0,
          width: 0,
          height: 0,
          runs: [],
        } as CapturedLine,
      })
      continue
    }
    const built = buildCapturedLine(group, options, innerW, 0, 0, ctx)
    if (!built) continue
    tentativeLines.push(built)
  }

  // Compute the font-metric baseline lift once, up front, so it applies
  // uniformly to every line. The drawCanvas painter anchors via
  // fontBoundingBoxAscent which overshoots the actual ink top — without
  // this lift the visual top of glyphs sits below where padTop says they
  // should. Applying it to line 0 only (the previous version) would
  // make every subsequent line sit too low by exactly that lift amount.
  const fallbackBaselineLift = (() => {
    if (hints.firstLineY !== null) return 0
    const fs = tentativeLines[0]?.maxFontSize ?? options.fontSize
    const font = `${options.fontStyle} ${options.fontWeight} ${fs}px "${options.fontFamily}"`
    const cssBaselineOffset = measureCssBaselineOffset(font, options.lineHeight)
    ctx.font = font
    const ascent = ctx.measureText('Hg').fontBoundingBoxAscent
    return cssBaselineOffset - ascent
  })()

  // Spacing per line: captured y-delta wins; otherwise maxFontSize × CSS lh.
  const lineYAt = (i: number, lines: BuiltLine[]): number => {
    if (hints.firstLineY !== null && hints.spacing !== null) {
      return hints.firstLineY + i * hints.spacing
    }
    if (hints.firstLineY !== null) {
      // Single captured line: use its y as the start, derive subsequent
      // spacing from the line's own font size and the CSS line-height.
      let y = hints.firstLineY
      for (let k = 0; k < i; k++) y += lines[k]!.maxFontSize * options.lineHeight
      return y
    }
    // Font-metric fallback — baseline lift applied uniformly so relative
    // spacing between lines matches CSS line-box stacking.
    let y = padTop + fallbackBaselineLift
    for (let k = 0; k < i; k++) y += lines[k]!.maxFontSize * options.lineHeight
    return y
  }

  // Apply vertical alignment when we're in the font-metric fallback path
  // (no captured firstLineY hint). When hints are present, alignment was
  // already baked into the captured `line.y` values by the browser at
  // extraction time, so we leave them alone. Without this, a node with
  // verticalAlign='center' or 'bottom' renders as if top-aligned the
  // moment its captured layout is invalidated (e.g. via a font-size or
  // alignment change in the toolbar).
  let verticalOffset = 0
  if (hints.firstLineY === null && tentativeLines.length > 0) {
    const last = tentativeLines[tentativeLines.length - 1]!
    const totalHeight =
      lineYAt(tentativeLines.length - 1, tentativeLines) -
      lineYAt(0, tentativeLines) +
      last.maxFontSize * options.lineHeight
    const slack = Math.max(0, innerH - totalHeight)
    if (options.verticalAlign === 'center') verticalOffset = slack / 2
    else if (options.verticalAlign === 'bottom') verticalOffset = slack
  }

  // Per-line `x` from captured hints is only valid when the rewrap produced
  // the same number of lines as the captured layout had. Otherwise the
  // index-based mapping is meaningless and would mix multi-flow offsets
  // (e.g. icon at x=0, body at x=59) with horizontalAlign-derived values.
  // When the count differs, fall through to the horizontalAlign-computed x
  // for every line.
  const useCapturedX = hints.lineXs !== null && hints.lineXs.length === tentativeLines.length

  for (let i = 0; i < tentativeLines.length; i++) {
    const tl = tentativeLines[i]!
    const y = lineYAt(i, tentativeLines) + verticalOffset
    const next = tentativeLines[i + 1]
    const heightFromNext =
      next && hints.spacing !== null ? hints.spacing : tl.maxFontSize * options.lineHeight
    const x = useCapturedX ? hints.lineXs![i]! : tl.line.x
    out.push({ ...tl.line, x, y, height: heightFromNext })
  }
  return out
}

/** Post-edit entry: given a text node's `content` runs, return a fresh
 *  `preWrappedLines` that the canvas fast path can paint directly. Returns
 *  null if the node has no content (caller can leave preWrappedLines unset). */
export function rewrapFromContent(
  node: LayoutNode & { data: { type: 'text' } },
  ctx: CanvasRenderingContext2D,
): CapturedLine[] | null {
  if (node.data.content.length === 0) return null
  const opts = node.data.options
  const padLeft = opts.paddingLeft ?? 0
  const padRight = opts.paddingRight ?? 0
  const padTop = opts.paddingTop ?? 0
  const padBottom = opts.paddingBottom ?? 0
  const innerW = Math.max(1, node.width - padLeft - padRight)
  const innerH = Math.max(1, node.height - padTop - padBottom)

  const flat = flatFromContent(node.data.content)
  const hints = inferLayoutHints(opts.preWrappedLines)
  const lines = buildLinesFromFlat(flat, opts, innerW, innerH, padTop, ctx, hints)
  return lines.length > 0 ? lines : null
}

/** Eval-spike entry: walk every text node, flatten its captured layout to
 *  runs, and re-wrap. Nodes without captured layout pass through unchanged. */
export function rewrapTextNodesFromRuns(
  nodes: LayoutNode[],
  ctx: CanvasRenderingContext2D,
): LayoutNode[] {
  return nodes.map((node) => {
    if (node.data.type !== 'text') return node
    const opts = node.data.options
    const captured = opts.preWrappedLines
    if (!captured || captured.length === 0) return node

    const padLeft = opts.paddingLeft ?? 0
    const padRight = opts.paddingRight ?? 0
    const padTop = opts.paddingTop ?? 0
    const padBottom = opts.paddingBottom ?? 0
    const innerW = Math.max(1, node.width - padLeft - padRight)
    const innerH = Math.max(1, node.height - padTop - padBottom)

    const flat = flattenLinesToRuns(captured, innerW, opts)
    const hints = inferLayoutHints(captured)
    const newLines = buildLinesFromFlat(flat, opts, innerW, innerH, padTop, ctx, hints)

    return {
      ...node,
      data: {
        ...node.data,
        options: {
          ...opts,
          preWrappedLines: newLines,
        },
      },
    }
  })
}
